import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { AccountData, AuditLogService, AuditType, PropelUserModel } from '../../api';
import { AuditLogData, isEventAuditLog, isOrderAuditLog, isPaymentLinkAuditLog, isVoucherAuditLog } from '../types';
import { RotatingLoader } from '../../common/ui/RotatingLoader';
import { getAuditTypeIcon } from '../utils';
import { AuditLogChangeDetails } from './AuditLogChangeDetails';
import { getDateFormat, getTimeFormat } from '../../common/getDateAndTimeFormat';
import { useAuth } from '../../session/InternalAuthProvider';

type AuditLogFlowVisualizationProps = {
  flowChainId: string;
  users: PropelUserModel[];
};

export const AuditLogFlowVisualization = ({ flowChainId, users }: AuditLogFlowVisualizationProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [flowData, setFlowData] = useState<AuditLogData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getAuditTypeLabel = (auditType: AuditType): string => {
    return t(`auditlog.audit_types.${auditType}`);
  };

  const fetchFlowData = async () => {
    setIsLoading(true);
    try {
      const response = await AuditLogService.getAuditLogsByFlowChainId(flowChainId);
      const flowLogs = (response as AuditLogData[]).sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );

      setFlowData(flowLogs);
    } catch (error) {
      console.error('Error fetching flow data:', error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (isDialogOpen && flowData.length === 0) {
      void fetchFlowData();
    }
  }, [isDialogOpen, flowData.length]);

  const hasChanges = (log: AuditLogData): boolean => {
    return (isOrderAuditLog(log) && log.changes?.length > 0) || (isEventAuditLog(log) && log.changes?.length > 0);
  };

  const getEntityText = (log: AuditLogData): string => {
    if (isOrderAuditLog(log)) {
      return t('auditlog.entity_info.order', { orderNumber: log.order_number });
    }
    if (isEventAuditLog(log)) {
      return t('auditlog.entity_info.event', { eventId: log.event_id });
    }
    if (isVoucherAuditLog(log)) {
      return t('auditlog.entity_info.voucher', { voucherId: log.voucher_id, orderNumber: log.order_number });
    }
    if (isPaymentLinkAuditLog(log)) {
      return t('auditlog.entity_info.payment_link', { paymentLinkNumber: log.payment_link_number });
    }
    return t('auditlog.entity_info.unknown');
  };

  const renderFlowSteps = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" p={3}>
          <RotatingLoader width="40px" />
        </Box>
      );
    }

    if (flowData.length === 0) {
      return <Typography color="textSecondary">{t('auditlog.flow.no_data')}</Typography>;
    }

    return (
      <Box>
        {flowData.map((log, index) => {
          const user = users.find((user: PropelUserModel) => user.user_id === log.user_id);
          const userLabel = user
            ? `${user.first_name} ${user.last_name}`
            : log.user_id
            ? t('auditlog.mbuser')
            : t('auditlog.customer');

          return (
            <Box key={`${log.created_at}-${index}`}>
              <Card variant="outlined">
                <CardContent
                  sx={{
                    py: isMobile ? 1.5 : 2,
                    px: isMobile ? 2 : 2.5,
                    '&:last-child': { pb: isMobile ? 1.5 : 2 },
                  }}>
                  <Box display="flex" alignItems="flex-start" gap={isMobile ? 2 : 2.5}>
                    <Box
                      sx={{
                        width: isMobile ? 28 : 32,
                        height: isMobile ? 28 : 32,
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: isMobile ? '12px' : '14px',
                        flexShrink: 0,
                        mt: 0.25,
                      }}>
                      {getAuditTypeIcon(log.audit_type)}
                    </Box>
                    <Box flex={1} minWidth={0}>
                      {/* Mobile Layout: Stack vertically */}
                      {isMobile ? (
                        <>
                          <Typography variant="body1" fontWeight="medium" sx={{ mb: 0.5, fontSize: '16px' }}>
                            {getAuditTypeLabel(log.audit_type)}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '14px' }}>
                              {moment(log.created_at).format(
                                `${getDateFormat(account).longDateFormat}  ${getTimeFormat(account)}`,
                              )}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '14px' }}>
                              • {userLabel}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            display="block"
                            sx={{ mb: hasChanges(log) ? 1 : 0, fontSize: '14px' }}>
                            {getEntityText(log)}
                          </Typography>
                        </>
                      ) : (
                        /* Desktop Layout: Horizontal */
                        <>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                            <Typography variant="subtitle1" noWrap sx={{ fontSize: '16px' }}>
                              {getAuditTypeLabel(log.audit_type)}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '14px' }}>
                              • {moment(log.created_at).format('MMM DD, HH:mm')}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '14px' }}>
                              • {userLabel}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body1"
                            color="textSecondary"
                            sx={{ mb: hasChanges(log) ? 1 : 0, fontSize: '14px' }}>
                            {getEntityText(log)}
                          </Typography>
                        </>
                      )}
                      {hasChanges(log) && <AuditLogChangeDetails changes={(log as any).changes} users={users} />}
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Flow arrow outside the box for all screen sizes */}
              {index < flowData.length - 1 && (
                <Box display="flex" justifyContent="center" py={1}>
                  <Typography variant="h6" color="textSecondary" sx={{ fontSize: '20px' }}>
                    ↓
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton size="small" onClick={() => setIsDialogOpen(true)} aria-label={t('auditlog.flow.view_details')}>
          <Visibility fontSize="small" />
        </IconButton>
      </Box>

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: isMobile ? {} : { maxHeight: '80vh' },
        }}>
        <DialogTitle sx={{ pb: isMobile ? 1 : 2 }}>{t('auditlog.flow.dialog.title')}</DialogTitle>
        <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
          <Typography variant="body1" color="textSecondary" gutterBottom sx={{ fontSize: isMobile ? '14px' : '16px' }}>
            {t('auditlog.flow.dialog.description', { flowChainId })}
          </Typography>
          {renderFlowSteps()}
        </DialogContent>
        <DialogActions sx={{ px: isMobile ? 2 : 3, pb: isMobile ? 2 : 1 }}>
          <Button onClick={() => setIsDialogOpen(false)}>{t('auditlog.flow.dialog.close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
